package org.gymly.util

/**
 * Extrae un nombre de archivo usable desde Content-Disposition.
 * Evita prefijos tipo `utf-8''` / `UTF-8''` que Spring suele emitir con filename*.
 */
fun parseContentDispositionFileName(
    disposition: String?,
    fallback: String,
): String {
    val header = disposition?.trim().orEmpty()
    if (header.isEmpty()) return sanitizeDownloadFileName(fallback)

    // Prefer RFC 5987 filename*=charset''value
    val starred = Regex(
        """(?:^|;)\s*filename\*\s*=\s*([^;]+)""",
        RegexOption.IGNORE_CASE,
    ).find(header)?.groupValues?.getOrNull(1)

    // Plain filename= (does not match filename*=)
    val plain = Regex(
        """(?:^|;)\s*filename\s*=\s*([^;]+)""",
        RegexOption.IGNORE_CASE,
    ).find(header)?.groupValues?.getOrNull(1)

    val raw = starred ?: plain
    return sanitizeDownloadFileName(raw?.let(::decodeContentDispositionValue) ?: fallback)
}

private fun decodeContentDispositionValue(raw: String): String {
    var value = raw.trim().trim('"')
    // RFC 5987: charset'lang'value  (ej. UTF-8''cuadre-de-caja.xlsx)
    val rfc5987 = Regex("""^([^']*)'[^']*'(.*)$""").matchEntire(value)
    if (rfc5987 != null) {
        value = rfc5987.groupValues[2]
    } else {
        value = value.replace(Regex("""^utf-8''""", RegexOption.IGNORE_CASE), "")
    }
    // RFC 2047 encoded-word: =?UTF-8?Q?...?=
    val rfc2047 = Regex("""^=\?([^?]+)\?([bq])\?(.+)\?=$""", RegexOption.IGNORE_CASE)
        .matchEntire(value)
    if (rfc2047 != null && rfc2047.groupValues[2].equals("Q", ignoreCase = true)) {
        value = rfc2047.groupValues[3]
            .replace("_", " ")
            .replace(Regex("""=([0-9A-Fa-f]{2})""")) { m ->
                m.groupValues[1].toIntOrNull(16)?.toChar()?.toString() ?: m.value
            }
    }
    return runCatching { decodeUriComponentLight(value) }.getOrDefault(value)
}

/** Decodifica %XX sin depender de platform URLDecoder. */
private fun decodeUriComponentLight(value: String): String {
    if (!value.contains('%')) return value
    val out = StringBuilder(value.length)
    var i = 0
    while (i < value.length) {
        val c = value[i]
        if (c == '%' && i + 2 < value.length) {
            val hex = value.substring(i + 1, i + 3)
            val code = hex.toIntOrNull(16)
            if (code != null) {
                out.append(code.toChar())
                i += 3
                continue
            }
        }
        out.append(c)
        i++
    }
    return out.toString()
}

fun sanitizeDownloadFileName(fileName: String): String {
    var name = fileName.trim().trim('"')
    name = name.replace(Regex("""^utf-8''""", RegexOption.IGNORE_CASE), "")
    name = name.substringAfterLast('/').substringAfterLast('\\')
    name = name.replace(Regex("""[\\/:*?"<>|]"""), "-")
    return name.ifBlank { "download" }
}
